import React, { useState, useMemo } from 'react'
import { useFormik } from "formik";
import * as yup from 'yup';
import { formatDate } from '../../utils/dateUtils';
import { userRequest } from '../../utils/requestMethods';
import { useStateContext } from '../../contexts/ContextProvider';
import ButtonLoading from '../ButtonLoading';
import { FiUser, FiMail, FiPhone, FiCalendar, FiMapPin, FiNavigation } from 'react-icons/fi';
import { Alert } from '../index';

const phoneRules = /^(0)\d{10}$/;
const DEFAULT_BVN = '00000000000';
const DEFAULT_DOB = '2024-01-01';
const DEFAULT_PERCENTAGE = '100';

const People = ({ official, setRefresh, services, onSaveSuccess }) => {
    const { school } = useStateContext();
    const [success, setSuccess] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [editingOfficialId, setEditingOfficialId] = useState(null);
    const isEditing = Boolean(editingOfficialId);

    const wantsFinancial = services?.includes('financial_management');

    const validationSchema = useMemo(() => {
        const base = {
            role: yup.string().required("Role is required"),
            title: yup.string().required("Job title is required"),
            first_name: yup.string().required("First name is required"),
            last_name: yup.string().required("Last name is required"),
            email: yup.string().email("Enter a valid email").required("Email is required"),
            phone_number: yup.string()
                .min(10, "Phone number must be at least 10 characters")
                .matches(phoneRules, { message: "Enter a valid phone number" })
                .required("Required"),
            address_line: yup.string().required("Address is required"),
            city: yup.string().required("City is required"),
            state: yup.string().required("State is required"),
            zip_code: yup.string()
                .matches(/^[0-9]{5,}$/, "Zip code must be at least 5 digits")
                .required("Zip code is required"),
        };

        if (wantsFinancial) {
            return yup.object().shape({
                ...base,
                bvn: yup.string()
                    .matches(/^[0-9]{11}$/, "BVN must be exactly 11 digits")
                    .required("BVN is required"),
                date_of_birth: yup.date()
                    .typeError("Invalid date format")
                    .required("Date of birth is required")
                    .max(new Date(), "Date cannot be in the future"),
                percentage: yup.string().required("Ownership percentage is required"),
            });
        }

        return yup.object().shape(base);
    }, [wantsFinancial]);

    const {
        values,
        errors,
        touched,
        handleBlur,
        handleChange,
        handleSubmit,
        resetForm,
        setFieldValue,
    } = useFormik({
        initialValues: {
            role: "",
            title: "",
            first_name: "",
            last_name: "",
            date_of_birth: "",
            email: "",
            phone_number: "",
            nationality: "NG",
            address_line: "",
            city: "",
            state: "",
            zip_code: "",
            percentage: "",
            bvn: "",
        },
        validationSchema,
        onSubmit: (values) => registerHandler(values),
    });

    const handleEditClick = (person) => {
        setFieldValue("role", person.role);
        setFieldValue("title", person.title);
        setFieldValue("first_name", person.first_name);
        setFieldValue("last_name", person.last_name);
        setFieldValue("date_of_birth", formatDate(person.date_of_birth));
        setFieldValue("email", person.email);
        setFieldValue("phone_number", person.phone_number);
        setFieldValue("nationality", person.nationality || "NG");
        setFieldValue("address_line", person.address?.address_line);
        setFieldValue("city", person.address?.city);
        setFieldValue("state", person.address?.state);
        setFieldValue("zip_code", person.address?.zip_code);
        setFieldValue("percentage", person.percentage);
        setFieldValue("bvn", person.bvn);
        setEditingOfficialId(person._id);
    };

    const safeDate = (val) => {
        if (!val) return null;
        const d = new Date(val);
        return isNaN(d.getTime()) ? null : formatDate(val);
    };

    const registerHandler = async (values) => {
        setLoading(true);
        try {
            const officialData = {
                role: values.role,
                title: values.title,
                first_name: values.first_name,
                last_name: values.last_name,
                email: values.email,
                phone_number: values.phone_number,
                nationality: values.nationality || "NG",
                address: {
                    address_line: values.address_line,
                    city: values.city,
                    state: values.state,
                    zip_code: values.zip_code,
                },
                bvn: wantsFinancial ? values.bvn : (values.bvn || DEFAULT_BVN),
                date_of_birth: wantsFinancial
                    ? safeDate(values.date_of_birth)
                    : (safeDate(values.date_of_birth) || DEFAULT_DOB),
                percentage: wantsFinancial
                    ? Number(values.percentage)
                    : (values.percentage ? Number(values.percentage) : Number(DEFAULT_PERCENTAGE)),
            };

            if (editingOfficialId) officialData._id = editingOfficialId;

            await userRequest.put(`/schools/${school._id}`, { officials: [officialData] });

            setSuccess(isEditing ? "Official updated successfully" : "Official added successfully");
            setTimeout(() => {
                setSuccess("");
                setRefresh("people");
                resetForm();
                setEditingOfficialId(null);
                if (!isEditing) onSaveSuccess?.();
            }, 2000);
        } catch (error) {
            console.log(error);
            setError("Something went wrong. Please try again.");
            setTimeout(() => setError(""), 5000);
        } finally {
            setLoading(false);
        }
    };

    const OfficialCard = ({ person }) => (
        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow dark:bg-gray-800 dark:border-gray-700">
            <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold text-xl">
                        {person.first_name?.[0]}{person.last_name?.[0]}
                    </div>
                    <div>
                        <h4 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                            {person.first_name} {person.last_name}
                        </h4>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mt-1">
                            {person.role || 'Administrator'}
                        </span>
                    </div>
                </div>
                <button
                    onClick={() => handleEditClick(person)}
                    type="button"
                    className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                >
                    Edit
                </button>
            </div>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                    <FiMail className="text-gray-400 shrink-0" /> {person.email || "-"}
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                    <FiPhone className="text-gray-400 shrink-0" /> {person.phone_number || "-"}
                </div>
                {wantsFinancial && (
                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                        <FiCalendar className="text-gray-400 shrink-0" /> {person.date_of_birth || "-"}
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <div className="max-w-4xl mx-auto">
            <div className="mb-8">
                <h3 className="text-2xl font-bold text-gray-800 dark:text-gray-200 mb-2">Who owns this school?</h3>
                <p className="text-gray-500 max-w-2xl">
                    Add the details of the school owners, directors, or significant administrators.
                </p>
            </div>

            {official?.length > 0 && (
                <div className="mb-10 grid grid-cols-1 gap-4">
                    {official.map((person, index) => (
                        <OfficialCard key={index} person={person} />
                    ))}
                </div>
            )}

            <div className="bg-white border border-gray-200 rounded-2xl p-8 dark:bg-gray-800 dark:border-gray-700">
                <div className="mb-6">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                        {isEditing ? "Edit Official" : "Add New Official"}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                        Please provide accurate details as they appear on official documents.
                    </p>
                </div>

                {success && <Alert type="success" message={success} />}
                {error && <Alert type="error" message={error} />}

                <form onSubmit={handleSubmit} className="space-y-5">
                    {/* Role row — always visible, ownership % only for financial */}
                    <div className={`grid grid-cols-1 gap-6 ${wantsFinancial ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
                        <div>
                            <label htmlFor="title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Job Title</label>
                            <select id="title" name="title" value={values.title} onChange={handleChange} onBlur={handleBlur} className="block w-full rounded-xl border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm py-3 px-4 bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-all">
                                <option value="">Select Job Title</option>
                                <option value="CEO">CEO</option>
                                <option value="COO">COO</option>
                                <option value="CFO">CFO</option>
                                <option value="President">President</option>
                                <option value="CIO">CIO</option>
                                <option value="VP">VP</option>
                                <option value="Treasurer">Treasurer</option>
                                <option value="Controller">Controller</option>
                                <option value="Manager">Manager</option>
                                <option value="Partner">Partner</option>
                                <option value="Member">Member</option>
                            </select>
                            {errors.title && touched.title && <p className="mt-1 text-sm text-red-600">{errors.title}</p>}
                        </div>

                        <div>
                            <label htmlFor="role" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Role / Position</label>
                            <select id="role" name="role" value={values.role} onChange={handleChange} onBlur={handleBlur} className="block w-full rounded-xl border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm py-3 px-4 bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-all">
                                <option value="">Select Role</option>
                                <option value="DIRECTOR">DIRECTOR</option>
                                <option value="OWNER">OWNER</option>
                            </select>
                            {errors.role && touched.role && <p className="mt-1 text-sm text-red-600">{errors.role}</p>}
                        </div>

                        {wantsFinancial && (
                            <div>
                                <label htmlFor="percentage" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Ownership %</label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <span className="text-gray-400 font-bold">%</span>
                                    </div>
                                    <input type="number" id="percentage" name="percentage" value={values.percentage} onChange={handleChange} onBlur={handleBlur} placeholder="e.g. 50" min="0" max="100" className="block w-full pl-10 rounded-xl border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm py-3 bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-all" />
                                </div>
                                {errors.percentage && touched.percentage && <p className="mt-1 text-sm text-red-600">{errors.percentage}</p>}
                            </div>
                        )}
                    </div>

                    {/* Name */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">First Name</label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><FiUser className="text-gray-400" /></div>
                                <input type="text" name="first_name" value={values.first_name} onChange={handleChange} onBlur={handleBlur} placeholder="First Name" className="block w-full pl-10 rounded-xl border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm py-3 bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                            </div>
                            {errors.first_name && touched.first_name && <p className="mt-1 text-sm text-red-600">{errors.first_name}</p>}
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Last Name</label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><FiUser className="text-gray-400" /></div>
                                <input type="text" name="last_name" value={values.last_name} onChange={handleChange} onBlur={handleBlur} placeholder="Last Name" className="block w-full pl-10 rounded-xl border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm py-3 bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                            </div>
                            {errors.last_name && touched.last_name && <p className="mt-1 text-sm text-red-600">{errors.last_name}</p>}
                        </div>
                    </div>

                    {/* Contact */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email Address</label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><FiMail className="text-gray-400" /></div>
                                <input type="email" name="email" value={values.email} onChange={handleChange} onBlur={handleBlur} placeholder="email@example.com" className="block w-full pl-10 rounded-xl border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm py-3 bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                            </div>
                            {errors.email && touched.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Phone Number</label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><FiPhone className="text-gray-400" /></div>
                                <input type="tel" name="phone_number" value={values.phone_number} onChange={handleChange} onBlur={handleBlur} placeholder="080..." className="block w-full pl-10 rounded-xl border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm py-3 bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                            </div>
                            {errors.phone_number && touched.phone_number && <p className="mt-1 text-sm text-red-600">{errors.phone_number}</p>}
                        </div>
                    </div>

                    {/* Financial-only fields: BVN, DOB */}
                    {wantsFinancial && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">BVN</label>
                                <input type="text" name="bvn" value={values.bvn} onChange={handleChange} onBlur={handleBlur} maxLength={11} className="block w-full rounded-xl border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm py-3 px-4 bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                                {errors.bvn && touched.bvn && <p className="mt-1 text-sm text-red-600">{errors.bvn}</p>}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date of Birth</label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><FiCalendar className="text-gray-400" /></div>
                                    <input type="date" name="date_of_birth" value={values.date_of_birth} onChange={handleChange} onBlur={handleBlur} className="block w-full pl-10 rounded-xl border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm py-3 bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                                </div>
                                {errors.date_of_birth && touched.date_of_birth && <p className="mt-1 text-sm text-red-600">{errors.date_of_birth}</p>}
                            </div>
                        </div>
                    )}

                    {/* Residential Address */}
                    <div className="pt-4 border-t border-gray-200 dark:border-gray-600">
                        <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Residential Address</h4>
                        <div className="grid grid-cols-1 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Address Line</label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><FiMapPin className="text-gray-400" /></div>
                                    <input type="text" name="address_line" value={values.address_line} onChange={handleChange} onBlur={handleBlur} className="block w-full pl-10 rounded-xl border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm py-3 bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                                </div>
                                {errors.address_line && touched.address_line && <p className="mt-1 text-sm text-red-600">{errors.address_line}</p>}
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">State</label>
                                    <select name="state" value={values.state} onChange={handleChange} onBlur={handleBlur} className="block w-full rounded-xl border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm py-3 px-4 bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-white">
                                        <option value="">Select State</option>
                                        <option value="ABIA">ABIA</option>
                                        <option value="ADAMAWA">ADAMAWA</option>
                                        <option value="AKWA_IBOM">AKWA IBOM</option>
                                        <option value="ANAMBRA">ANAMBRA</option>
                                        <option value="BAUCHI">BAUCHI</option>
                                        <option value="BAYELSA">BAYELSA</option>
                                        <option value="BENUE">BENUE</option>
                                        <option value="BORNO">BORNO</option>
                                        <option value="CROSS_RIVER">CROSS RIVER</option>
                                        <option value="DELTA">DELTA</option>
                                        <option value="EBONYI">EBONYI</option>
                                        <option value="EDO">EDO</option>
                                        <option value="EKITI">EKITI</option>
                                        <option value="ENUGU">ENUGU</option>
                                        <option value="FCT">FCT - ABUJA</option>
                                        <option value="GOMBE">GOMBE</option>
                                        <option value="IMO">IMO</option>
                                        <option value="JIGAWA">JIGAWA</option>
                                        <option value="KADUNA">KADUNA</option>
                                        <option value="KANO">KANO</option>
                                        <option value="KATSINA">KATSINA</option>
                                        <option value="KEBBI">KEBBI</option>
                                        <option value="KOGI">KOGI</option>
                                        <option value="KWARA">KWARA</option>
                                        <option value="LAGOS">LAGOS</option>
                                        <option value="NASARAWA">NASARAWA</option>
                                        <option value="NIGER">NIGER</option>
                                        <option value="OGUN">OGUN</option>
                                        <option value="ONDO">ONDO</option>
                                        <option value="OSUN">OSUN</option>
                                        <option value="OYO">OYO</option>
                                        <option value="PLATEAU">PLATEAU</option>
                                        <option value="RIVERS">RIVERS</option>
                                        <option value="SOKOTO">SOKOTO</option>
                                        <option value="TARABA">TARABA</option>
                                        <option value="YOBE">YOBE</option>
                                        <option value="ZAMFARA">ZAMFARA</option>
                                    </select>
                                    {errors.state && touched.state && <p className="mt-1 text-sm text-red-600">{errors.state}</p>}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">City</label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><FiNavigation className="text-gray-400" /></div>
                                        <input type="text" name="city" value={values.city} onChange={handleChange} onBlur={handleBlur} className="block w-full pl-10 rounded-xl border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm py-3 bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                                    </div>
                                    {errors.city && touched.city && <p className="mt-1 text-sm text-red-600">{errors.city}</p>}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Zip Code</label>
                                    <input type="text" name="zip_code" value={values.zip_code} onChange={handleChange} onBlur={handleBlur} className="block w-full rounded-xl border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm py-3 px-4 bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                                    {errors.zip_code && touched.zip_code && <p className="mt-1 text-sm text-red-600">{errors.zip_code}</p>}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end pt-4">
                        {loading ? (
                            <ButtonLoading styles="inline-flex justify-center py-2 px-8 border border-transparent shadow-sm text-md font-medium rounded-full text-white bg-indigo-400" />
                        ) : (
                            <button type="submit" className="inline-flex justify-center py-3 px-6 border border-transparent shadow-sm text-sm font-medium rounded-full text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all w-full md:w-auto">
                                {isEditing ? "Update Official" : "Save Official"}
                            </button>
                        )}
                    </div>
                </form>
            </div>
        </div>
    )
}

export default People
